import React, { useEffect, useState } from "react";
import { toast } from "sonner";
import { SlidersHorizontal, FileLock2 } from "lucide-react";
import api, { formatApiError } from "@/pages/cauzioni/cauApi";
import CauShell from "@/pages/cauzioni/CauShell";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Switch } from "@/components/ui/switch";

export default function CauSettings() {
  const { user } = useAuth();
  const [cfg, setCfg] = useState(null);
  const [busy, setBusy] = useState(false);

  useEffect(() => { api.get("/config").then((r) => setCfg(r.data)).catch(() => setCfg({})); }, []);

  if (user?.role !== "admin")
    return <CauShell><div className="mx-auto max-w-[1400px] px-6 py-16 text-center text-slate-500" data-testid="settings-forbidden">Sezione riservata agli amministratori.</div></CauShell>;
  if (!cfg) return <CauShell><div className="p-16 text-center text-slate-400">Caricamento…</div></CauShell>;

  const save = async () => {
    if (Number(cfg.sogliaMinima) >= Number(cfg.sogliaAlta)) { toast.error("La soglia minima deve essere inferiore alla soglia alta"); return; }
    setBusy(true);
    try {
      const { data } = await api.put("/config", {
        sogliaAlta: Number(cfg.sogliaAlta), sogliaMinima: Number(cfg.sogliaMinima), enforceDocObbligatori: !!cfg.enforceDocObbligatori,
      });
      setCfg(data); toast.success("Impostazioni salvate");
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setBusy(false); }
  };

  return (
    <CauShell>
      <div className="mx-auto max-w-3xl px-6 py-8" data-testid="cau-settings-page">
        <h1 className="font-display text-3xl font-extrabold text-slate-900">Impostazioni</h1>
        <p className="text-sm text-slate-500 mt-1 mb-8">Configurazione del motore di scoring e delle regole documentali. Utenti e ruoli si gestiscono dal pannello Admin della Suite.</p>

        <div className="space-y-6">
          <section className="bg-white border border-slate-200 rounded-sm p-6">
            <div className="flex items-center gap-2 mb-4"><FileLock2 size={18} className="text-slate-700" /><h2 className="font-display font-bold text-slate-900">Obbligo documentale</h2></div>
            <div className="flex items-center justify-between rounded-sm border border-slate-200 px-4 py-3.5">
              <div>
                <div className="text-sm font-medium text-slate-800">Documenti obbligatori bloccanti</div>
                <div className="text-xs text-slate-400 max-w-md">Se attivo, la pratica non può andare in relazione finale senza i documenti obbligatori (Visura, Bilancio, Disciplinare, Documento identità, Delega). Disattiva per simulazioni rapide.</div>
              </div>
              <Switch checked={!!cfg.enforceDocObbligatori} onCheckedChange={(v) => setCfg({ ...cfg, enforceDocObbligatori: v })} data-testid="settings-enforce-docs" />
            </div>
            <div className={`mt-3 text-xs ${cfg.enforceDocObbligatori ? "text-emerald-700" : "text-amber-700"}`}>
              {cfg.enforceDocObbligatori ? "Modalità produzione: obbligo documentale attivo." : "Modalità simulazione: documenti non bloccanti."}
            </div>
          </section>

          <section className="bg-white border border-slate-200 rounded-sm p-6">
            <div className="flex items-center gap-2 mb-4"><SlidersHorizontal size={18} className="text-slate-700" /><h2 className="font-display font-bold text-slate-900">Soglie di scoring (su 100)</h2></div>
            <div className="grid sm:grid-cols-2 gap-4">
              <div className="space-y-1.5">
                <label className="text-sm font-medium text-slate-700">Soglia alta (Proponibile ≥)</label>
                <Input type="number" min={1} max={100} value={cfg.sogliaAlta ?? ""} onChange={(e) => setCfg({ ...cfg, sogliaAlta: e.target.value })} data-testid="settings-soglia-alta" />
              </div>
              <div className="space-y-1.5">
                <label className="text-sm font-medium text-slate-700">Soglia minima (Con mitigazioni ≥)</label>
                <Input type="number" min={1} max={100} value={cfg.sogliaMinima ?? ""} onChange={(e) => setCfg({ ...cfg, sogliaMinima: e.target.value })} data-testid="settings-soglia-minima" />
              </div>
            </div>
            <p className="text-xs text-slate-400 mt-3">Proponibile ≥ {cfg.sogliaAlta} · Con mitigazioni {cfg.sogliaMinima}–{Number(cfg.sogliaAlta) - 1} · Non proponibile &lt; {cfg.sogliaMinima}</p>
          </section>

          <div className="flex justify-end">
            <Button onClick={save} disabled={busy} className="rounded-sm bg-slate-900 hover:bg-slate-800" data-testid="settings-save-button">{busy ? "Salvataggio…" : "Salva impostazioni"}</Button>
          </div>
        </div>
      </div>
    </CauShell>
  );
}
